import { CheckCircle2, CircleDashed, Circle, MinusCircle, TrendingUp } from 'lucide-react';
import type { PublishStatusCell } from '@/lib/types-publish-gate';

/**
 * v0.3 (S-7): 公開状態を表す状態アイコン + 色。
 * Design C1対応: 詳細設計書は bg-brand-green / bg-brand-yellow / bg-brand-gray を使用していたが
 * tailwind.config.js に未定義 (実在は success, alert, misespark, text-muted 等のトークン)。
 * 実在トークンに揃える (success=公開済み、alert=一部入力中、surface-sunken=未着手、text-muted=対象外)。
 * v0.4 章⑤ (D-15/D-126): preliminary (速報公開) を追加。published と同じ「可視」だが確度が異なる
 * ことを色・アイコンの両方で区別する (§9-3「確度表示を3つの出口すべてに必須化する」)。
 */
export type CellDisplayStatus = 'published' | 'preliminary' | 'partial' | 'draft_none' | 'not_applicable';

export const resolveCellDisplayStatus = (cell: PublishStatusCell): CellDisplayStatus => {
  if (cell.status === 'published') return 'published';
  if (cell.status === 'preliminary') return 'preliminary';
  const { total_data_types, ready_count, not_applicable_count } = cell.input_ready_summary;
  if (total_data_types === 0 && not_applicable_count > 0) return 'not_applicable';
  if (ready_count > 0) return 'partial';
  return 'draft_none';
};

const STYLES: Record<CellDisplayStatus, { icon: typeof CheckCircle2; iconClass: string; bgClass: string; label: string }> = {
  published: { icon: CheckCircle2, iconClass: 'text-success-text', bgClass: 'bg-success-bg', label: '公開済み' },
  // ⚠️ design-and-code MEDIUM 指摘 (D-186): misespark.orange (#FE6A03) は alert.bg (#FDF1E2) 上で
  // 4.5:1 のコントラストを満たさない (UsersPage.tsx で既に一度踏んだのと同型の再発)。
  // 「確定 (success/green) と視覚的に分ける」意図は alert.text (#96590A、alert.bg 用に調整済み)
  // + アイコン形状 (TrendingUp) の組で引き続き満たされる。
  preliminary: { icon: TrendingUp, iconClass: 'text-alert-text', bgClass: 'bg-alert-bg', label: '速報公開' },
  partial: { icon: CircleDashed, iconClass: 'text-alert-text', bgClass: 'bg-alert-bg', label: '一部入力中' },
  draft_none: { icon: Circle, iconClass: 'text-text-muted', bgClass: 'bg-surface-sunken', label: '未着手' },
  // 中間REVIEW (S-12 Design) HIGH対応: draft_noneと同一トークンだとアイコン形状のみでの区別になり
  // WCAG 1.4.11 (非テキストコントラスト3:1) を満たさない。not_applicableはより濃い text-sub にして
  // 色でも区別できるようにする (text-sub:#6E6862 は text-muted:#9A938D より暗く判別しやすい)
  not_applicable: { icon: MinusCircle, iconClass: 'text-text-sub', bgClass: 'bg-surface-sunken', label: '対象外' },
};

export const StatusIcon = ({ status, size = 16 }: { status: CellDisplayStatus; size?: number }) => {
  const s = STYLES[status];
  const Icon = s.icon;
  return <Icon size={size} className={s.iconClass} aria-hidden />;
};

export const statusLabel = (status: CellDisplayStatus): string => STYLES[status].label;
export const statusBgClass = (status: CellDisplayStatus): string => STYLES[status].bgClass;

/** Matrix凡例用: 全状態の一覧
 *  ⚠️ platform-reviewer 指摘 (D-186): 'preliminary' (D-15/D-126) が漏れていた。凡例に出ないだけで
 *  実データの表示自体は STYLES 側で正しく preliminary を扱えていたため気づかれにくいバグだった。 */
export const ALL_STATUSES: CellDisplayStatus[] = ['published', 'preliminary', 'partial', 'draft_none', 'not_applicable'];
